import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { roleLabel } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function formatDateTime(dateString) {
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(dateString));
}

function formatActionLabel(action) {
  return String(action || "")
    .split(".")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " "))
    .join(" / ");
}

function describeDetails(entry) {
  const details = entry.details || {};
  const parts = [];

  if (details.sourceSessionTitle) {
    parts.push(`Källa: ${details.sourceSessionTitle}`);
  }
  if (details.stationTitle) {
    parts.push(`Station: ${details.stationTitle}`);
  }
  if (typeof details.fromRole === "number" && typeof details.toRole === "number") {
    parts.push(`Roll: ${roleLabel(details.fromRole)} -> ${roleLabel(details.toRole)}`);
  }
  if (typeof details.pathCount === "number") {
    parts.push(`Sökvägar: ${details.pathCount}`);
  }
  if (typeof details.stationCount === "number") {
    parts.push(`Stationer: ${details.stationCount}`);
  }
  if (details.clipPath) {
    parts.push(`Video: ${details.clipPath}`);
  }

  return parts.join(" | ");
}

function AuditTrail() {
  const [days, setDays] = useState(30);
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/audit-trail`, {
      headers: {
        "Content-Type": "application/json",
        token: localStorage.getItem("token")
      },
      params: {
        days,
        action: selectedAction
      }
    })
      .then((response) => {
        setEntries(response.data.entries || []);
        setActions(response.data.actions || []);
        setErrorMessage("");
      })
      .catch((error) => {
        setErrorMessage(error.response?.data?.message || "Kunde inte hämta revisionsloggen");
      });
  }, [days, selectedAction]);

  return (
    <div className="flex flex-col items-center px-3">
      <div className="w-full max-w-5xl py-4">
        <div className="theme-card">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Administration</p>
              <h1 className="mt-2 text-lg font-bold text-slate-800">Revisionslogg</h1>
              <p className="mt-2 text-sm text-slate-600">
                Spårar viktiga ändringar i användare, pass, metadata och administratörsåtgärder.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/admin-dashboard">
                <button className="rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100">
                  Till administration
                </button>
              </Link>
              <select
                value={days}
                onChange={(event) => setDays(parseInt(event.target.value, 10))}
                className="rounded-[20px] border border-sky-200 bg-white px-5 py-3 text-sm text-slate-800 shadow-sm outline-none transition-colors focus:border-sky-400"
              >
                <option value={7}>Senaste 7 dagarna</option>
                <option value={30}>Senaste 30 dagarna</option>
                <option value={90}>Senaste 90 dagarna</option>
              </select>
              <select
                value={selectedAction}
                onChange={(event) => setSelectedAction(event.target.value)}
                className="rounded-[20px] border border-sky-200 bg-white px-5 py-3 text-sm text-slate-800 shadow-sm outline-none transition-colors focus:border-sky-400"
              >
                <option value="">Alla åtgärder</option>
                {actions.map((action) => (
                  <option key={action} value={action}>
                    {formatActionLabel(action)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {errorMessage && (
            <div className="theme-alert-error mt-4 py-3">
              {errorMessage}
            </div>
          )}

          {!errorMessage && (
            <div className="mt-5 space-y-4">
              {entries.length === 0 && (
                <div className="theme-empty-state rounded-[22px] px-4">
                  Inga revisionshändelser hittades för det här urvalet.
                </div>
              )}

              {entries.map((entry) => {
                const detailsText = describeDetails(entry);

                return (
                  <div
                    key={entry._id}
                    className="rounded-[22px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 px-4 py-4 shadow-sm"
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-sky-700">
                            {formatActionLabel(entry.action)}
                          </span>
                          {entry.targetType && (
                            <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                              {entry.targetType}
                            </span>
                          )}
                        </div>

                        <p className="mt-3 text-base font-semibold text-slate-800">
                          {entry.targetName || "Ingen titel"}
                        </p>

                        <p className="mt-1 text-sm text-slate-600">
                          Utförd av {entry.actorName || "Okänd användare"} ({roleLabel(entry.actorRole)})
                        </p>

                        {detailsText && (
                          <p className="mt-3 break-words text-sm text-slate-500">
                            {detailsText}
                          </p>
                        )}
                      </div>

                      <p className="shrink-0 text-sm font-medium text-sky-700">
                        {formatDateTime(entry.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuditTrail;
